(() => {
  const ROOT_SELECTOR = '[data-deeb-header-luxury]';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
  const controllers = new WeakMap();

  class DeebLuxuryHeader {
    constructor(root) {
      this.root = root;
      this.products = root.querySelector('[data-deeb-luxury-products]');
      this.trigger = root.querySelector('[data-deeb-products-trigger]');
      this.panel = root.querySelector('[data-deeb-products-panel]');
      this.drawerDetails = document.getElementById('Details-menu-drawer-container');
      this.closeTimer = 0;
      this.drawerWasOpen = false;

      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onDrawerToggle = this.onDrawerToggle.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);

      this.init();
    }

    init() {
      if (!this.products || !this.trigger || !this.panel) return;

      this.products.addEventListener('pointerenter', this.onPointerEnter);
      this.products.addEventListener('pointerleave', this.onPointerLeave);
      this.products.addEventListener('focusin', this.onFocusIn);
      this.products.addEventListener('focusout', this.onFocusOut);
      this.products.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('pointerdown', this.onDocumentPointerDown, true);

      if (this.drawerDetails) {
        this.drawerDetails.addEventListener('toggle', this.onDrawerToggle);
      }

      desktop.addEventListener?.('change', this.onViewportChange);
      this.syncDrawerBodyState();
    }

    openProducts() {
      if (!desktop.matches) return;
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = 0;
      }

      this.products.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    closeProducts({ returnFocus = false } = {}) {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = 0;
      }

      this.products.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');

      if (returnFocus) {
        this.trigger.focus({ preventScroll: true });
      }
    }

    scheduleClose() {
      if (this.closeTimer) window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        this.closeTimer = 0;
        if (!this.products.matches(':hover') && !this.products.contains(document.activeElement)) {
          this.closeProducts();
        }
      }, 150);
    }

    onPointerEnter() {
      if (hoverCapable.matches) this.openProducts();
    }

    onPointerLeave() {
      if (hoverCapable.matches) this.scheduleClose();
    }

    onFocusIn() {
      this.openProducts();
    }

    onFocusOut() {
      window.requestAnimationFrame(() => {
        if (!this.products.contains(document.activeElement) && !this.products.matches(':hover')) {
          this.scheduleClose();
        }
      });
    }

    onKeyDown(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.closeProducts({ returnFocus: true });
    }

    onDocumentPointerDown(event) {
      if (!this.products.classList.contains('is-open')) return;
      if (this.products.contains(event.target)) return;
      this.closeProducts();
    }

    syncDrawerBodyState() {
      const isOpen = Boolean(this.drawerDetails?.open) && desktop.matches;
      document.body.classList.toggle('deeb-header-drawer-open', isOpen);

      if (isOpen && !this.drawerWasOpen) {
        this.closeProducts();
      }

      this.drawerWasOpen = isOpen;
    }

    onDrawerToggle() {
      this.syncDrawerBodyState();
    }

    onViewportChange() {
      if (!desktop.matches) {
        this.closeProducts();
        document.body.classList.remove('deeb-header-drawer-open');
      } else {
        this.syncDrawerBodyState();
      }
    }

    destroy() {
      if (this.closeTimer) window.clearTimeout(this.closeTimer);

      this.products?.removeEventListener('pointerenter', this.onPointerEnter);
      this.products?.removeEventListener('pointerleave', this.onPointerLeave);
      this.products?.removeEventListener('focusin', this.onFocusIn);
      this.products?.removeEventListener('focusout', this.onFocusOut);
      this.products?.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
      this.drawerDetails?.removeEventListener('toggle', this.onDrawerToggle);
      desktop.removeEventListener?.('change', this.onViewportChange);

      document.body.classList.remove('deeb-header-drawer-open');
      controllers.delete(this.root);
    }
  }

  const init = (scope = document) => {
    const roots = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : [...(scope.querySelectorAll?.(ROOT_SELECTOR) || [])];

    roots.forEach((root) => {
      if (controllers.has(root)) return;
      controllers.set(root, new DeebLuxuryHeader(root));
    });
  };

  init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);

    controllers.get(root)?.destroy();
  });
})();
